import { useMemo } from 'react';

import {
   HealthcareProviderLocation,
   HealthcareProviderLocationIdentifier,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { ValidationRules } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { useLocationAssociationWithDocplannerValidator } from '~legacy.docplanner';

import { personnelTranslationKeys } from '../../../../translations';
import { AvailabilityCreationFormBean } from '../../../availabilityCreation/availabilityCreation.types';
import { AvailabilityUpdateFormBean } from '../../../availabilityUpdate/availabilityUpdate.types';
import { AvailabilityFormController } from '../../common.types';

import { HealthcareProviderLocationSelectItem } from './HealthcareProviderLocationSelectItem';

type AvailabilityHealthcareProviderLocationSelectProps = {
   readonly register: AvailabilityFormController['register'];
   readonly defaultLocationIdentifier?: HealthcareProviderLocationIdentifier;
   readonly healthcareLocations: HealthcareProviderLocation[];
};

const triggerLabelExtractor = (selectedHealthcareLocationProvider: HealthcareProviderLocation): string =>
   selectedHealthcareLocationProvider.name;

const availabilityHealthcareProviderLocationSelectKeyExtrator = (location: HealthcareProviderLocation): number =>
   location.locationIdentifier.locationId;

const getDefaultValue = (
   healthcareLocations: HealthcareProviderLocation[],
   defaultLocationIdentifier?: HealthcareProviderLocationIdentifier
): HealthcareProviderLocation | undefined => {
   return healthcareLocations.find(
      ({ locationIdentifier: { locationId, type } }) =>
         locationId === defaultLocationIdentifier?.locationId && type === defaultLocationIdentifier?.type
   );
};

const useValidationRules = (): ValidationRules<AvailabilityCreationFormBean | AvailabilityUpdateFormBean> => {
   const validateLocationAssociationWithDocplanner = useLocationAssociationWithDocplannerValidator();
   return useMemo(() => {
      return {
         required: true,
         // eslint-disable-next-line @typescript-eslint/require-await
         validate: async (data, getValue) => {
            const sendToDocplanner = getValue('sendToDocplanner');
            const healthcareProviderLocation = data as HealthcareProviderLocation;
            if (!sendToDocplanner) {
               return undefined;
            }
            return validateLocationAssociationWithDocplanner(healthcareProviderLocation.locationIdentifier);
         },
      };
   }, [validateLocationAssociationWithDocplanner]);
};

export const AvailabilityHealthcareProviderLocationSelect = (
   props: AvailabilityHealthcareProviderLocationSelectProps
): JSX.Element => {
   return (
      <Select<HealthcareProviderLocation>
         testId={'healthcareProviderLocation'}
         triggerLabelExtractor={triggerLabelExtractor}
         controller={props.register('healthcareProviderLocation', useValidationRules())}
         placeholder={personnelTranslationKeys.choose}
         label={personnelTranslationKeys.healthcareProviderLocation}
         defaultValue={getDefaultValue(props.healthcareLocations, props.defaultLocationIdentifier)}
      >
         <Select.List<HealthcareProviderLocation>
            items={props.healthcareLocations}
            keyExtractor={availabilityHealthcareProviderLocationSelectKeyExtrator}
         >
            {(location) => <HealthcareProviderLocationSelectItem location={location} />}
         </Select.List>
      </Select>
   );
};
